import { SideMenuLink } from '@amzn/meridian/side-menu';
import Text from '@amzn/meridian/text';
import {
  getMicroFrontendSubModuleName,
  getMicroFrontendSubmodulePath,
  MicroFrontends,
} from '@amzn/rangoli-seller-desktop-utils/src/index';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { helpAndSupportStore } from '../../store/HelpAndSupportStore';
import { sidebarStore } from '../../store/SidebarStore';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Theme from '@amzn/meridian/theme/theme';
import mobileIcon from "../../assets/Icons/MobileIcon.svg"
import Tooltip from '@amzn/meridian/tooltip';
import { SidebarContent, ModulePaths, parentPaths } from '../../constants/SideBarConstants';
import { iconBlueColorStyle, newTagSpacing, parentSideMenuColor, sideMenuMargin } from '../../Style/Style';
import { newTagToken, sidemenuTextColorToken, toolTipToken } from '../../Style/tokens';

export interface IChildSidebarMenuLinkProps {
  parentMicrofrontendKey?: MicroFrontends;
  subModuleKey: string;
  subModulePath?: string;
  isNewFeature?: boolean;
  isNotAvailableInDesktop?: boolean;
}

const SidebarChildMenuLink = ({
  parentMicrofrontendKey,
  subModuleKey,
  subModulePath,
  isNewFeature,
  isNotAvailableInDesktop
}: IChildSidebarMenuLinkProps): React.JSX.Element => {
  const navigate = useNavigate();
  const { handleChangeActiveProfileDetails } = helpAndSupportStore;
  const { childRoute, parentRoute, setSidebarToggle, setParentRoute, setChildRoute } = sidebarStore;
  const subModuleText = parentMicrofrontendKey ? getMicroFrontendSubModuleName(parentMicrofrontendKey, subModuleKey): subModuleKey;

  const handleOnClickNavigation = (): void => {
    // Since the Returns configuration has been moved to the delivery section, it no longer falls under the OMS according to the Sidemenu, so an additional check and change in route has done.
    if (parentMicrofrontendKey && subModuleKey !== SidebarContent.returnsConfig) {
        const [parent, child] = getMicroFrontendSubmodulePath(parentMicrofrontendKey, subModuleKey, true).split("/");
        setParentRoute(parent);
        setChildRoute(child);
        navigate(`${parent}/${child}`);
    } else {
    // Checking for other routes which doesn't have parent MFE
        if (subModuleText === SidebarContent.returnsSettings) {
            setChildRoute(SidebarContent.config);
            subModulePath = SidebarContent.config;
            navigate(`${SidebarContent.orders}/${SidebarContent.returns}/${SidebarContent.config}`);
        } else {
            setChildRoute(subModulePath);
            navigate(subModulePath);
        }
        const parentPath = ModulePaths.find(path => path.includes(subModulePath))?.[0]?.toLowerCase();
        if (parentPath) setParentRoute(parentPath);
    }
    handleChangeActiveProfileDetails(false);
    setSidebarToggle();
};

const handleChildMenuItem = (): boolean => {
  const isNotInParentPaths = !parentPaths.includes(parentRoute);
  const normalizedSubModuleText = subModuleText.toLowerCase().replace(/\s+/g, '');
  const normalizedChildRoute = childRoute.replace(/-/g, '');
  const isReturnsRoute =
    isNotInParentPaths && childRoute === SidebarContent.returns && subModuleText === SidebarContent.manageReturns;
  const isReturnsSettingsConfigRoute =
    isNotInParentPaths && childRoute === SidebarContent.config && subModuleText === SidebarContent.returnsSettings;
  const isAllOrdersMainRoute =
    isNotInParentPaths && childRoute === '' && subModuleText === SidebarContent.allOrders;
  const isMatchingSubmoduleRoute =
    isNotInParentPaths &&
    (normalizedSubModuleText != SidebarContent.returns && normalizedSubModuleText === normalizedChildRoute || subModulePath === childRoute);
  return (
    isReturnsSettingsConfigRoute ||
    isAllOrdersMainRoute ||
    isMatchingSubmoduleRoute || 
    isReturnsRoute
  );
};

  return (
    <SideMenuLink onClick={handleOnClickNavigation} disabled={isNotAvailableInDesktop}>
      {isNotAvailableInDesktop ? (
        <Theme tokens={toolTipToken}>
          <Tooltip position="bottom" title={SidebarContent.mobileIconToolTipContent} id="myTooltip">
            <Row className={sideMenuMargin} spacing="200" aria-describedby="myTooltip">
              <Text
                type={"b200"}
                className={handleChildMenuItem() ? iconBlueColorStyle : parentSideMenuColor}
              >
                {subModuleText}
              </Text>
              {isNewFeature && (
                <Theme tokens={newTagToken}>
                  <Column backgroundColor="#FFF4D9" className={newTagSpacing} type="fill">
                    <Text type="b300" color="primary">
                      {SidebarContent.newTag}
                    </Text>
                  </Column>
                </Theme>
              )}
              <img src={mobileIcon} />
            </Row>
          </Tooltip>
        </Theme>
      ) : (
        <Row className={sideMenuMargin} spacing="200" aria-describedby="myTooltip">
          <Theme tokens={sidemenuTextColorToken}>
            <Text 
              type={handleChildMenuItem() ? "h100" : "b200"} 
              className={handleChildMenuItem() ? iconBlueColorStyle : parentSideMenuColor}
            >
              {subModuleText}
            </Text>
          </Theme>
          {isNewFeature && (
            <Theme tokens={newTagToken}>
              <Column backgroundColor="#FFF4D9" className={newTagSpacing} type="fill">
                <Text type="b300" color="primary">
                  {SidebarContent.newTag}
                </Text>
              </Column>
            </Theme>
          )}
        </Row>
      )}
    </SideMenuLink>
  );
};

export default observer(SidebarChildMenuLink);
